import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import { Stack, Typography } from '@mui/material';
import { createFileRoute, useRouter } from '@tanstack/react-router';

import { LoadingSpinner } from 'src/components/LoadingSpinner';
import auth0Service from 'src/services/auth0';
import { setBearerToken, setIdToken } from 'src/store/slices/authSlice';
import { useAppDispatch } from 'src/store/store';

// Use hardcoded path for route definition - TanStack Router needs static analysis
// This must match the file path: routes/(auth)/auth-confirmed.tsx
export const Route = createFileRoute('/(auth)/auth-confirmed')({
    component: RouteComponent,
});

function RouteComponent() {
    const dispatch = useAppDispatch();
    const { latestLocation, navigate } = useRouter();
    const { hash } = latestLocation;

    const { isAuthenticated, isLoading: isLoadingAuth, getAccessTokenSilently } = useAuth0();

    // Ref to track if we've already processed the hash to prevent double execution
    const hasProcessedHash = useRef(false);

    const [isParsingHash, setIsParsingHash] = useState(false);

    const handlePasswordlessRedirect = useCallback(
        async (hash: string) => {
            try {
                setIsParsingHash(true);

                const parsedResult = await auth0Service.parseHash({ hash });
                dispatch(setBearerToken(parsedResult.authResult?.accessToken));
                dispatch(setIdToken(parsedResult.authResult?.idToken));

                // This is necessary so that the @auth0/auth0-react library can update its internal state
                // to match the result from the auth0-js library
                try {
                    await getAccessTokenSilently();
                } catch (error) {
                    console.error('Error getting access token silently:', error);
                    navigate({ to: '/login' });
                }
            } catch (error) {
                console.error('Error parsing hash:', error);
                navigate({ to: '/login' });
            } finally {
                setIsParsingHash(false);
            }
        },
        [dispatch, getAccessTokenSilently, navigate]
    );

    const isLoading = isLoadingAuth || isParsingHash;

    /**
     * Expected cycles through this effect:
     * 1. Initial load when the component mounts, is loading auth
     * 2. Auth0 is loaded but is not authenticated. If hash is present, it processes the hash
     * 3. After Auth0 authentication completes, isAuthenticated becomes true and redirects to home
     */
    useEffect(() => {
        if (isLoadingAuth) return;

        if (!isLoadingAuth && isAuthenticated) {
            navigate({ to: '/home', replace: true });
        }

        if (!isAuthenticated && !isLoadingAuth && hash && !hasProcessedHash.current) {
            handlePasswordlessRedirect(hash);
            hasProcessedHash.current = true;
        }
    }, [isAuthenticated, isLoadingAuth, hash, navigate, handlePasswordlessRedirect]);

    return (
        <Stack
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100dvh',
                gap: 2,
            }}
        >
            {/* TODO: loading experience */}
            {(isLoading || isAuthenticated) && <LoadingSpinner />}

            {/* TODO: unauthenticated experience */}
            {!isLoading && !isAuthenticated && (
                <Typography variant="body1">Not Authenticated</Typography>
            )}
        </Stack>
    );
}
